import { debounce } from "lodash-es";
import { nextTick, watch } from "vue";

import { sendStickyNoteAction, useEventBus } from "@/composables/useEventBus";
import type { Board } from "@/model/board";
import { isDependency } from "@/model/stickyType";

import { useBoardStore } from "../board";
import { highlightCard, resetCardMark } from "../helpers/mark";
import { useLinkStore } from "../link";
import { matchesCard } from "./CardMatcher";
import type { SearchMenuState } from "./searchMenu";
import { useSearchMenuStore } from "./searchMenu";
import { useSearchResultsStore } from "./searchResults";

const { onBoardLoaded, onBoardSwitch, onStickyNoteAction } = useEventBus({ neverUnregister: true });

const SEARCH_DEBOUNCE_MS = 150;
const debouncedApplySearch = debounce(applySearch, SEARCH_DEBOUNCE_MS, {
  leading: false,
  trailing: true,
});

export function initSearchBoardSync() {
  onBoardLoaded(async (boardId: Board["id"]) => {
    if (useBoardStore().currentBoard().id === boardId) {
      await nextTick(); // Temp - remove when sticky states are calculated in the backend
      applySearch();
    }
  });

  onBoardSwitch(applySearch);

  // Re-evaluate the filter only when the changed sticky belongs to the current board
  // TODO could be optimized by only matching the changed sticky to the search
  onStickyNoteAction(null, "changed", ({ id }) => {
    if (useBoardStore().board?.cards[id]) {
      debouncedApplySearch();
    }
  });

  watch(() => useSearchMenuStore().$state, applyQueryChange, { deep: true });
  watch(() => useSearchMenuStore().anyFilterCriteria, applyAnyQueryChange);
  watch(() => useBoardStore().enlargedStickyNoteId, applyMarkedStickyChange);
  watch(() => useLinkStore().isMarkingLinks, applyMarkingLinkChange);
}

function applyQueryChange(search: SearchMenuState) {
  if (search.isSearchOpen) {
    // shrink the enlarged sticky note if search criteria changed
    const id = useBoardStore().enlargedStickyNoteId;
    if (id) {
      sendStickyNoteAction(id, { action: "shrink" });
    }
  }

  applySearch();
}

function applyAnyQueryChange(filterCriteria: boolean) {
  if (!filterCriteria) {
    useLinkStore().updateCardLinkedMarks();
  }
}

function applyMarkedStickyChange(cardId: string | null) {
  if (cardId === null) {
    useSearchResultsStore().unsetCurrent();
  }
}

function applyMarkingLinkChange(marking: boolean) {
  if (useSearchMenuStore().anyFilterCriteria && !marking) {
    applySearch();
  }
}

let previousAnyFilterCriteria = false; // To determine if an update removed filters

function applySearch() {
  const anyFilterCriteria = useSearchMenuStore().anyFilterCriteria;
  // Performance optimization: skip update if the search sidebar is closed
  // and there are no changes to the filter criteria (no current or previous criteria)
  if (!useSearchMenuStore().isSearchOpen && !anyFilterCriteria && !previousAnyFilterCriteria) {
    return;
  }
  previousAnyFilterCriteria = anyFilterCriteria;

  const board = useBoardStore().currentBoard();
  useSearchResultsStore().search(board.cards, (card) => matchesCard(board, card));

  if (useSearchMenuStore().isDependencySearch) {
    highlightDependencyLinkedStickies(board);
  }

  // Highlight the selected feature when upper level links filter is active
  if (useSearchMenuStore().selectedUpperLevelLinks.type === "sticky") {
    highlightSelectedFeature(board);
  }
}

/** highlights the stickies which are related to a dependency that matches the search */
function highlightDependencyLinkedStickies(board: Board) {
  useSearchResultsStore().found.forEach((result) => {
    const card = result.card;
    if (!card || !isDependency(card.data)) {
      return;
    }
    card.data.linkedStickies.forEach((link) => {
      const linkedCard = useLinkStore().boardCardByLink(link.stickyId, board);
      resetCardMark(linkedCard);
    });
  });
}

/** highlights the selected feature when filtering by upper level links */
function highlightSelectedFeature(board: Board) {
  const selection = useSearchMenuStore().selectedUpperLevelLinks;
  if (selection.type !== "sticky") {
    return;
  }

  // Find the feature card on the current board and highlight it
  const featureCard = board.cards[selection.stickyId];
  if (featureCard) {
    highlightCard(featureCard);
  }
}
